/**
 * RemoveCalibrationDialog - delete a calibration (so a botched one can be
 * remade), or only unlink it from one mix design.
 *
 * Asks the server what a delete would remove and what stops it
 * (GET .../removal, the same rules DELETE enforces) before offering it.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import calibrationsService, { CalibrationRemovalCheck } from '../../services/calibrations';
import productsService from '../../services/products';
import { apiErrorText } from '../../utils/apiError';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export interface RemoveCalibrationDialogProps {
  /** The calibration to remove; null = closed. */
  calibrationId: string | null;
  /** The mix design the user is on. With linkId, "Unlink from this mix only" is offered. */
  productId?: string;
  linkId?: string;
  /** The user may delete calibrations (mix_designs.edit_maturity_curve). */
  canDelete: boolean;
  /** The user may unlink (mix_designs.link_activate). */
  canUnlink: boolean;
  onClose: () => void;
  onDeleted: (message: string) => void;
  onUnlinked?: (message: string) => void;
}

const RemoveCalibrationDialog: React.FC<RemoveCalibrationDialogProps> = ({
  calibrationId, productId, linkId, canDelete, canUnlink, onClose, onDeleted, onUnlinked,
}) => {
  const [check, setCheck] = useState<CalibrationRemovalCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCheck(null);
    setError(null);
    if (!calibrationId) return;
    let cancelled = false;
    setLoading(true);
    calibrationsService.removalCheck(calibrationId)
      .then((c) => { if (!cancelled) setCheck(c); })
      .catch((e) => { if (!cancelled) setError(apiErrorText(e, 'Could not check whether this calibration can be deleted')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [calibrationId]);

  const label = check ? (check.name || check.code) : 'this calibration';
  const thisLink = check?.linked_mixes.find((m) => m.product_id === productId);
  const otherMixes = (check?.linked_mixes ?? []).filter((m) => m.product_id !== productId);
  const showUnlink = canUnlink && !!productId && !!linkId && !!onUnlinked;
  const unlinkBlocked = !!thisLink?.is_active;

  const doDelete = async () => {
    if (!calibrationId) return;
    setBusy(true);
    setError(null);
    try {
      await calibrationsService.delete(calibrationId);
      onDeleted(`${label} deleted`);
    } catch (e) {
      setError(apiErrorText(e, 'The calibration could not be deleted'));
    } finally {
      setBusy(false);
    }
  };

  const doUnlink = async () => {
    if (!productId || !linkId) return;
    setBusy(true);
    setError(null);
    try {
      await productsService.deleteCalibrationLink(productId, linkId);
      onUnlinked?.(`${label} unlinked from this mix design`);
    } catch (e) {
      setError(apiErrorText(e, 'The calibration could not be unlinked'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!calibrationId} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove {label}?</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {check && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {!canDelete ? (
              <Alert severity="info">You do not have permission to delete calibrations.</Alert>
            ) : check.can_delete ? (
              <>
                <Typography>
                  Deleting removes the calibration and{' '}
                  {check.test_results > 0 ? <strong>its {plural(check.test_results, 'test result')}</strong> : 'its details'}{' '}
                  for good. You can then start a new calibration for this mix.
                </Typography>
                {otherMixes.length > 0 && (
                  <Alert severity="warning">
                    It is also linked to {otherMixes.length === 1 ? 'mix' : 'mixes'}{' '}
                    <strong>{otherMixes.map((m) => m.code || m.product_id).join(', ')}</strong>, and is removed from{' '}
                    {otherMixes.length === 1 ? 'it' : 'them'} too.
                  </Alert>
                )}
              </>
            ) : (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ mb: 0.5 }}>This calibration cannot be deleted:</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {check.blockers.map((b) => <li key={b}><Typography variant="body2">{b}</Typography></li>)}
                </Box>
              </Alert>
            )}
            {showUnlink && (
              <Typography variant="body2" color="text.secondary">
                {unlinkBlocked
                  ? 'It is active on this mix, so it cannot be unlinked either until another calibration is made active.'
                  : 'Unlink only removes it from this mix design. The calibration and its test results are kept and can be linked again.'}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {showUnlink && (
          <Button onClick={doUnlink} disabled={busy || loading || !check || unlinkBlocked}>
            Unlink from this mix only
          </Button>
        )}
        {canDelete && (
          <Button
            variant="contained"
            color="error"
            onClick={doDelete}
            disabled={busy || loading || !check?.can_delete}
          >
            Delete calibration
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RemoveCalibrationDialog;
